import { useEffect, useMemo, useRef, useState } from 'react';

export type AutocompleteOption = {
  value: string;
  label: string;
  /** Secondary line (e.g. the client's email) — also matched by search. */
  sublabel?: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  disabled?: boolean;
  buttonClassName?: string;
  /** Shown in the dropdown when the search matches nothing. */
  emptyText?: string;
};

/**
 * Searchable single-select dropdown (type-to-filter combobox), themed to
 * match the pro portal. Replaces the plain <select> on the client-rates
 * form where the client/service lists can be long.
 */
export function AutocompleteSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  buttonClassName = '',
  emptyText = 'No matches.',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  // Close on outside click; focus the search box when opening.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      clearTimeout(t);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={`relative ${buttonClassName}`} ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-full w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 text-left text-[14px] outline-none transition disabled:opacity-60 ${
          open
            ? 'border-[#8e2a2f] ring-1 ring-[#8e2a2f]'
            : 'border-[#eee] hover:border-[#d8c9c0] focus:border-[#8e2a2f]'
        }`}
      >
        <span
          className={`truncate ${
            selected ? 'font-bold text-[#3a3335]' : 'text-[#908e8e]'
          }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span
          className={`material-icons text-[20px] shrink-0 text-[#908e8e] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        >
          expand_more
        </span>
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-[#eee] bg-white shadow-lg">
          <div className="flex items-center gap-1.5 border-b border-[#f1eae6] px-3 py-2">
            <span className="material-icons text-[18px] text-[#908e8e]">
              search
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              autoComplete="off"
              spellCheck={false}
              className="w-full bg-transparent text-[14px] outline-none placeholder:text-[#908e8e]"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-[#908e8e]">
                {emptyText}
              </li>
            ) : (
              filtered.map((o) => {
                const isSelected = o.value === value;
                return (
                  <li key={o.value || '__placeholder__'}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(o.value);
                      }}
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-[#faf6f3] ${
                        isSelected ? 'bg-[#8e2a2f]/[0.06]' : ''
                      }`}
                    >
                      <span
                        className={`text-[14px] ${
                          isSelected
                            ? 'font-bold text-[#8e2a2f]'
                            : 'text-[#3a3335]'
                        }`}
                      >
                        {o.label}
                      </span>
                      {o.sublabel ? (
                        <span className="text-[12px] text-[#908e8e]">
                          {o.sublabel}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
